import cn from "classnames";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BOARDS_INSIGHT_LOADING,
  BOARD_CARD_LOADING,
} from "../../constants/loading.constant";
import {
  ANALYTICS_EXPAND,
  BOARD_CARD_MODAL,
  BOARD_FILTERS_MODAL,
  BOARD_METRIC_MODAL,
} from "../../constants/modal.constant";
import { useBoardAnalyticsCore } from "../../cores/boardAnalytics";
import { ChartMetric } from "../../cores/boardAnalytics/interface/ChartMetric";
import {
  processTotalsMetric,
  toLineMetric,
  toPieMetric,
} from "../../cores/boardAnalytics/utils/processTotalsMetric";
import { useBoardCore } from "../../cores/boards";
import { getCurrencySymbol } from "../../cores/boards/constants/currencies.constant";
import { useGlobalCore } from "../../cores/globals";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { CrmChartCard } from "../component/analytics/CrmChartCard";
import { CrmChartLegend } from "../component/analytics/CrmChartLegend";
import { CrmKpiRow } from "../component/analytics/CrmKpiRow";
import { BarChart } from "../component/analytics/charts/BarChart";
import { CrmDonutChart } from "../component/analytics/charts/CrmDonutChart";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { useIntl } from "react-intl";
import { CL$Format } from "../../cores/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BoardSelector from "../component/boards/BoardSelector";
import FiltersForm from "../component/boards/modal/FiltersForm";
import Opportunities from "../component/boards/modal/Opportunities";
import moment from "moment";
import TaskSideModals from "../component/tasks/TaskSideModals";
import BoardSideModals from "../component/boards/BoardSideModals";
import CompanySideModals from "../component/contactCompanies/CompanySideModals";
import ContactSideModals from "../component/contacts/ContactSideModals";
import EventSideModals from "../component/event/EventSideModals";
import EmailSideModals from "../component/gmail/EmailSideModals";
import { Position } from "../../cores/globals/interfaces/globalEntity";
import { CARD_PRODUCT } from "../../constants/internalModal.constant";
import { Errors } from "../../constants/errors";
import { CardStatusOptions } from "../../cores/boards/constants/comment-type";
import { useConfigCore } from "../../cores/config";
import { InputWembii } from "../component/InputWembii";
import { TaskStatus } from "../../cores/tasks/config";
import "../component/analytics/CrmMetrics.scss";
import { WEMBII_BRAND } from "../component/analytics/crmAnalytics.constants";

const percentFormat = (value: number) => `${value}%`;

export const BusinessBoardAnalyticsScreen = () => {
  const intl = useIntl();
  const { company, companyPlan, companyRole, isAdmin, isReseller } =
    useMeCore();
  const {
    isModal,
    turnOnModal,
    turnOffModal,
    getModalVisibility,
    turnOffInternalModal,
    isLoading,
    getModalContent,
  } = useGlobalCore();
  const { goToSocial } = useRouterCore();
  const { commentTypes, getCommentTypes } = useConfigCore();
  const {
    boardBusiness: board,
    filters,
    errorCode,
    getBusinessBoards,
    cleanCard,
    getFilters,
    getBusinessBoard,
  } = useBoardCore();
  const {
    getCrmInsights,
    toChartMetric,
    setDate,
    setHasMore,
    date,
    crmInsights,
  } = useBoardAnalyticsCore();
  const [commentType, setCommentType] = useState("all");
  const [taskType, setTaskType] = useState("all");

  useEffect(() => {
    if (
      !companyPlan?.rules.allowBusinessBoard ||
      (!companyRole?.crm &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    )
      goToSocial();
  }, [companyPlan, companyRole, isAdmin, isReseller, goToSocial]);

  useEffect(() => {
    getBusinessBoards(company?.id!);
    getCommentTypes(company?.id!);
  }, [company, getBusinessBoards, getCommentTypes]);

  const cardByDayData = useMemo(
    () => toLineMetric(crmInsights.cardsByDay),
    [crmInsights.cardsByDay]
  );
  const cardsByListData = useMemo(
    () => toPieMetric(crmInsights.cardsByList),
    [crmInsights.cardsByList]
  );
  const cardsByStatusData = useMemo(
    () => toPieMetric(crmInsights.cardsByStatus),
    [crmInsights.cardsByStatus]
  );
  const budgetByListData = useMemo(
    () => toPieMetric(crmInsights.budgetByList, CL$Format),
    [crmInsights.budgetByList]
  );
  const budgetByStatusData = useMemo(
    () => toPieMetric(crmInsights.budgetByStatus, CL$Format),
    [crmInsights.budgetByStatus]
  );
  const cardsByUserData = useMemo(
    () => toPieMetric(crmInsights.cardsByUser),
    [crmInsights.cardsByUser]
  );
  const averageBudgetByListData = useMemo(
    () => toPieMetric(crmInsights.averageBudgetByList, CL$Format),
    [crmInsights.averageBudgetByList]
  );
  const winRateByListData = useMemo(
    () => toPieMetric(crmInsights.winRateByList, percentFormat),
    [crmInsights.winRateByList]
  );
  const dealExpirationData = useMemo(
    () => toPieMetric(crmInsights.dealExpiration),
    [crmInsights.dealExpiration]
  );
  const activitiesByDayData = useMemo(
    () => toLineMetric(crmInsights.activitiesByDay),
    [crmInsights.activitiesByDay]
  );
  const commentsByUserData = useMemo(
    () => processTotalsMetric(crmInsights.commentsByUser, commentType),
    [crmInsights.commentsByUser, commentType]
  );
  const tasksByUserData = useMemo(
    () => processTotalsMetric(crmInsights.tasksByUser, taskType),
    [crmInsights.tasksByUser, taskType]
  );

  const cardByDayChartMetrics: ChartMetric = useMemo(
    () => toChartMetric([cardByDayData]),
    [cardByDayData, toChartMetric]
  );
  const activitiesByDayChartMetrics: ChartMetric = useMemo(
    () => toChartMetric([activitiesByDayData]),
    [activitiesByDayData, toChartMetric]
  );

  const typesValues = useMemo(
    () => [
      { value: "all", label: intl.formatMessage({ id: "app.source.all" }) },
      ...CardStatusOptions.map((status) => ({
        value: status.value,
        label: intl.formatMessage({ id: status.label }),
      })),
      ...commentTypes.map((type) => ({ value: type.id, label: type.name })),
    ],
    [commentTypes, intl]
  );

  const taskTypesValues = useMemo(
    () => [
      { value: "all", label: intl.formatMessage({ id: "app.source.all" }) },
      {
        value: TaskStatus.Completed,
        label: intl.formatMessage({ id: "app.tasks.completed" }),
      },
      {
        value: TaskStatus.Incompleted,
        label: intl.formatMessage({ id: "app.tasks.incompleted" }),
      },
    ],
    [intl]
  );

  const pieCharts = useMemo(
    () => [
      {
        id: "cardsByList",
        title: intl.formatMessage({ id: "app.metricsCrm.oportunitys1" }),
        data: cardsByListData,
        chart: toChartMetric([cardsByListData]),
      },
      {
        id: "cardsByStatus",
        title: intl.formatMessage({ id: "app.metricsCrm.cardsByStatus" }),
        data: cardsByStatusData,
        chart: toChartMetric([cardsByStatusData]),
      },
      {
        id: "cardsByUser",
        title: intl.formatMessage({ id: "app.metricsCrm.cardsByUser" }),
        data: cardsByUserData,
        chart: toChartMetric([cardsByUserData]),
      },
      {
        id: "budgetByList",
        title: `${intl.formatMessage({
          id: "app.metricsCrm.oportunitys2",
        })} (${board?.currency && getCurrencySymbol(board.currency)})`,
        data: budgetByListData,
        chart: toChartMetric([budgetByListData]),
      },
      {
        id: "budgetByStatus",
        title: intl.formatMessage({ id: "app.metricsCrm.budgetByStatus" }),
        data: budgetByStatusData,
        chart: toChartMetric([budgetByStatusData]),
      },
      {
        id: "averageBudgetByList",
        title: intl.formatMessage({ id: "app.metricsCrm.averageBudgetByList" }),
        data: averageBudgetByListData,
        chart: toChartMetric([averageBudgetByListData]),
      },
      {
        id: "winRateByList",
        title: intl.formatMessage({ id: "app.metricsCrm.winRateByList" }),
        data: winRateByListData,
        chart: toChartMetric([winRateByListData]),
      },
      {
        id: "dealExpiration",
        title: intl.formatMessage({ id: "app.metricsCrm.dealExpiration" }),
        data: dealExpirationData,
        chart: toChartMetric([dealExpirationData]),
      },
      {
        id: "commentsByUser",
        title: intl.formatMessage({ id: "app.metricsCrm.oportunitys3" }),
        data: commentsByUserData,
        chart: toChartMetric([commentsByUserData]),
        selector: (
          <InputWembii
            type="select"
            onChange={setCommentType}
            value={commentType}
            mb={false}
          >
            {typesValues.map(({ label, value }) => (
              <option key={label} value={value}>
                {label}
              </option>
            ))}
          </InputWembii>
        ),
      },
      {
        id: "tasksByUser",
        title: intl.formatMessage({ id: "app.metricsCrm.tasksByUser" }),
        data: tasksByUserData,
        chart: toChartMetric([tasksByUserData]),
        selector: (
          <InputWembii
            type="select"
            onChange={setTaskType}
            value={taskType}
            mb={false}
          >
            {taskTypesValues.map(({ label, value }) => (
              <option key={label} value={value}>
                {label}
              </option>
            ))}
          </InputWembii>
        ),
      },
    ],
    [
      intl,
      cardsByListData,
      cardsByStatusData,
      cardsByUserData,
      budgetByListData,
      budgetByStatusData,
      averageBudgetByListData,
      winRateByListData,
      dealExpirationData,
      commentsByUserData,
      tasksByUserData,
      board?.currency,
      commentType,
      taskType,
      toChartMetric,
      typesValues,
      taskTypesValues,
    ]
  );

  const toggleFilterModal = useCallback(() => {
    turnOnModal(
      BOARD_FILTERS_MODAL,
      <FiltersForm
        board={board}
        onFilter={(filters, onSuccess) => {
          if (board) {
            getCrmInsights(company?.id!, board.id, filters, onSuccess);
          }
        }}
        onClearFilter={() => {
          if (board) {
            getCrmInsights(company?.id!, board.id, {});
          }
        }}
      />
    );
  }, [turnOnModal, getCrmInsights, company, board]);

  const onSearching = useCallback(
    (companyId: string, boardId: string) => {
      const filter = getFilters(boardId);
      getBusinessBoard(companyId, boardId, filter, false);
      getCrmInsights(companyId, boardId, filter);
    },
    [getCrmInsights, getFilters, getBusinessBoard]
  );

  const isFiltered = useMemo(() => {
    return filters[`${board?.id!}`];
  }, [filters, board]);

  const showBarCards = useCallback(
    (element) => {
      if (!element.length) return;
      const metricDate = cardByDayChartMetrics.labels[element[0]._index];

      if (date !== metricDate) {
        setDate(metricDate);
      }

      setHasMore(true);
      turnOnModal(BOARD_METRIC_MODAL, true, Position.LEFT);
    },
    [cardByDayChartMetrics, date, setDate, setHasMore, turnOnModal]
  );

  const totalOpportunitiesBadge = useMemo(() => {
    const total = crmInsights.crmSummary?.values?.find(
      (v) => v.label === "totalOpportunities"
    )?.value;
    if (typeof total !== "number") return undefined;
    return String(intl.formatMessage(
      { id: "app.metricsCrm.totalBadge" },
      { count: total }
    ));
  }, [crmInsights.crmSummary, intl]);

  const formatBarLabels = useCallback(
    (labels: string[]) =>
      labels.map((date) => {
        const parsed = moment(date);
        if (!parsed.isValid()) return date;
        return parsed.format("DD MMM");
      }),
    []
  );

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({ id: "app.metricsCrm.title" })}
        rightChildren={
          <>
            <BoardSelector onSearching={onSearching} />
            <button
              className={cn("ml-3 btn", {
                whiteButton: !isFiltered,
                "btn-primary": isFiltered,
              })}
              onClick={toggleFilterModal}
            >
              <FontAwesomeIcon icon={["fas", "sliders-h"]} className="mr-2" />
              {intl.formatMessage({ id: "app.crmBusiness.filters" })}
            </button>
          </>
        }
      />
      {isLoading(BOARDS_INSIGHT_LOADING) ? (
        <div className="crm-metrics-loading">
          <UpdateLoadingCard
            loadingId={BOARDS_INSIGHT_LOADING}
            text={intl.formatMessage({ id: "app.metricsCrm.updating" })}
          />
          <p className="crm-metrics-loading__text">
            {intl.formatMessage({ id: "app.metricsCrm.updatingDescription" })}
          </p>
        </div>
      ) : (
        <Content>
          <div className="crm-metrics-page">
            <section className="crm-metrics-section">
              <h2 className="crm-metrics-section__title">
                {intl.formatMessage({ id: "app.metricsCrm.sectionOverview" })}
              </h2>
              <CrmKpiRow
                summary={crmInsights.crmSummary}
                currency={board?.currency}
              />
            </section>

            <section className="crm-metrics-section">
              <h2 className="crm-metrics-section__title">
                {intl.formatMessage({ id: "app.metricsCrm.sectionTrends" })}
              </h2>

              <div className="crm-charts-grid crm-charts-grid--trends">
                <CrmChartCard
                  id="crmChartOpportunitiesByDay"
                  title={intl.formatMessage({ id: "app.metricsCrm.oportunitys" })}
                  badge={totalOpportunitiesBadge}
                  variant="bar"
                  className="crm-chart-card--compact"
                  isEmpty={
                    !cardByDayData.values || cardByDayData.values.length === 0
                  }
                >
                  <BarChart
                    labels={formatBarLabels(cardByDayChartMetrics.labels)}
                    datasets={cardByDayChartMetrics.datasets.map((dataset) => ({
                      ...dataset,
                      color: WEMBII_BRAND,
                    }))}
                    onElementClick={showBarCards}
                    height={180}
                  />
                </CrmChartCard>

                <CrmChartCard
                  id="crmChartActivitiesByDay"
                  title={intl.formatMessage({
                    id: "app.metricsCrm.activitiesByDay",
                  })}
                  variant="bar"
                  className="crm-chart-card--compact"
                  isEmpty={
                    !activitiesByDayData.values ||
                    activitiesByDayData.values.length === 0
                  }
                >
                  <BarChart
                    labels={formatBarLabels(activitiesByDayChartMetrics.labels)}
                    datasets={activitiesByDayChartMetrics.datasets.map(
                      (dataset) => ({
                        ...dataset,
                        color: WEMBII_BRAND,
                      })
                    )}
                    height={180}
                  />
                </CrmChartCard>
              </div>
            </section>

            <section className="crm-metrics-section">
              <h2 className="crm-metrics-section__title">
                {intl.formatMessage({
                  id: "app.metricsCrm.sectionDistribution",
                })}
              </h2>

              <div className="crm-charts-grid">
                {pieCharts.map(({ id, title, data, chart, selector }) => (
                  <CrmChartCard
                    key={id}
                    id={id}
                    title={title}
                    selector={selector}
                    variant="donut"
                    isEmpty={!data.values || data.values.length === 0}
                    legend={<CrmChartLegend metric={data} />}
                  >
                    <CrmDonutChart
                      labels={chart.labels}
                      datasets={chart.datasets}
                    />
                  </CrmChartCard>
                ))}
              </div>
            </section>
          </div>

          <SideModal
            active={isModal(ANALYTICS_EXPAND)}
            visible={getModalVisibility(ANALYTICS_EXPAND)}
            onClose={() => turnOffModal(ANALYTICS_EXPAND)}
            left
          >
            {getModalContent(ANALYTICS_EXPAND)}
          </SideModal>
          <SideModal
            active={isModal(BOARD_FILTERS_MODAL)}
            visible={getModalVisibility(BOARD_FILTERS_MODAL)}
            onClose={() => turnOffModal(BOARD_FILTERS_MODAL)}
          >
            {getModalContent(BOARD_FILTERS_MODAL)}
          </SideModal>
          <SideModal
            active={isModal(BOARD_METRIC_MODAL)}
            visible={getModalVisibility(BOARD_METRIC_MODAL)}
            onClose={() => {
              turnOffModal(BOARD_METRIC_MODAL);
              setHasMore(false);
            }}
            left
          >
            <Opportunities />
          </SideModal>
          <SideModal
            active={isModal(BOARD_CARD_MODAL)}
            visible={getModalVisibility(BOARD_CARD_MODAL)}
            onClose={() => {
              turnOffModal(BOARD_CARD_MODAL);
              turnOffInternalModal(CARD_PRODUCT);
              cleanCard();
            }}
            showSppiner={isLoading(BOARD_CARD_LOADING)}
            showError={errorCode === Errors.CardLoad}
          >
            {getModalContent(BOARD_CARD_MODAL)}
          </SideModal>

          <TaskSideModals />
          <ContactSideModals />
          <CompanySideModals />
          <BoardSideModals />
          <EventSideModals />
          <EmailSideModals />
        </Content>
      )}
    </Layout>
  );
};
